import { fetchToken, MetaLayerDefinition, useLayers } from '@agissc/ktag-map';
import { useQueries, useQuery } from '@tanstack/react-query';
import config from '../../../utils/AppConfig';

export interface TopicLayerConfiguration {
  subLayerId: number;
  documentationUrl: string;
  displayInLegend: boolean;
  attributeSearchAllowed: boolean;
  searchableAttributes: Record<string, any>;
  queryableAttributes: Record<string, any>;
}

interface DatensatzELementLayer {
  id: number;
  index: number;
  existsInTOC: boolean;
  suche: boolean;
  searchableAttributes: Record<string, any>;
  queryableAttributes: Record<string, any>;
}

interface TopicLayerConfigurationDetail {
  datensatzelementLayers: DatensatzELementLayer[];
  startupMessage: string;
  hasStartupMessage: boolean;
}

interface TopicLayerProps {
  id: string;
  isPublic: boolean;
}

const fetchTopicLayerConfiguration = async (topicLayer: string, isPublic: boolean) => {
  let baseurl = config.apis.layerConfig;

  const headers = new Headers();

  if (!isPublic) {
    const token = await fetchToken();
    headers.set('Authorization', `Bearer ${token}`);
    baseurl = `${baseurl}/secured`;
  }

  const topicLayerName = topicLayer.split('::').shift();
  const metadataDetailResponse = await fetch(`${baseurl}/${topicLayerName}`, {
    headers,
  });
  const metadataDetailResult: TopicLayerConfigurationDetail =
    await metadataDetailResponse.json();

  return metadataDetailResult;
};

const queryBuilder = <TData = TopicLayerConfigurationDetail>(
  topicLayer: string,
  isPublic: boolean,
  isEnabled: boolean,
  select?: (data: TopicLayerConfigurationDetail) => TData,
) => ({
  queryKey: ['layerConfiguration', topicLayer, isPublic, isEnabled],
  queryFn: () => fetchTopicLayerConfiguration(topicLayer, isPublic),
  select,
  enabled: isEnabled,
  gcTime: 3600000,
  staleTime: Infinity,
});

const getLayerQueryParams = (
  topicLayer: string,
  layersQuery: {
    data: Record<string, MetaLayerDefinition>;
    isLoading: boolean;
    error: Error | null | undefined;
  },
) => {
  if (!topicLayer || layersQuery.isLoading) return { isEnabled: false, isPublic: false };
  return {
    isEnabled:
      layersQuery.data[topicLayer]?.source === 'topicmaps.geo.ag.ch' &&
      layersQuery.data[topicLayer]?.isPublic !== undefined,
    isPublic: layersQuery.data[topicLayer]?.isPublic === true,
  };
};

const useMetadataQueryBase = <TData = TopicLayerConfigurationDetail>(
  topicLayer: string,
  select?: (data: TopicLayerConfigurationDetail) => TData,
) => {
  const layersQuery = useLayers();
  const { isEnabled, isPublic } = getLayerQueryParams(topicLayer, layersQuery);
  return useQuery(queryBuilder(topicLayer ?? '', isPublic, isEnabled, select));
};

const selectMetadata = (metadataDetail: TopicLayerConfigurationDetail) => {
  return metadataDetail.datensatzelementLayers.reduce(
    (lookup: Record<number, TopicLayerConfiguration>, item) => {
      return {
        ...lookup,
        [item.index]: {
          subLayerId: item.index,
          documentationUrl: `https://www.ag.ch/geoportal/geodatenshop/Datendokumentation.aspx?Datensatzelement=${item.id}`,
          displayInLegend: item.existsInTOC,
          attributeSearchAllowed: item.suche,
          searchableAttributes: item.searchableAttributes,
          queryableAttributes: item.queryableAttributes,
        },
      };
    },
    {},
  );
};

export const useMetadataQuery = (topicLayer: string) =>
  useMetadataQueryBase(topicLayer, (data) => selectMetadata(data));

export const useMetadataQueryStartupMessage = (topicLayer: string) =>
  useMetadataQueryBase(topicLayer, (data) =>
    data.hasStartupMessage ? data.startupMessage : null,
  );

export const useMetadataQueries = (topicLayers: TopicLayerProps[]) => {
  const queries = topicLayers.map((item) =>
    queryBuilder(item.id, item.isPublic, true, (data: TopicLayerConfigurationDetail) =>
      selectMetadata(data),
    ),
  );

  return useQueries({
    queries,
    combine: (results) => {
      return {
        data: results.reduce(
          (acc, result, index) => {
            const { id } = topicLayers[index];
            if (result.data) {
              return { ...acc, [id]: { ...result.data } };
            }
            return acc;
          },
          {} as Record<string, Record<string, TopicLayerConfiguration>>,
        ),
        isLoading: results.some((result) => result.isLoading),
        error: results.find((result) => result.error)?.error,
      };
    },
  });
};
